import { Children } from 'react';
import { cn } from '@/ui/design-system/utils/cn';

/**
 * The top of a row drawer: an optional dimension eyebrow, the entity name, one
 * line of context under it, and the actions cluster on the right.
 *
 * `contextLabel` is the drawer's single context slot — the subcategory on an
 * explore drawer, the parent entity on a related-entity drawer. A related drawer
 * passes `contextPrefix="in"` so the line reads as a relationship rather than a
 * label — the prefix stays lower-case and only the entity name is capitalised.
 */
function DrawerHeader({
    name,
    eyebrow,
    contextLabel,
    contextPrefix,
    capitalize = true,
    className,
    children,
}) {
    // Call sites pass conditional actions, so `children` is often an array like
    // [false, null] — truthy, but with nothing to render. That would still draw the
    // wrapper and cost the row a `gap-3`, narrowing the name beside it.
    const actions = Children.toArray(children);
    return (
        <div className={cn('px-4 pt-5', className)}>
            <div className="flex items-start justify-between gap-3">
                <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                    {eyebrow ? (
                        <span className="flex items-center gap-1 text-sm font-semibold capitalize text-muted-foreground">
                            {eyebrow}
                        </span>
                    ) : null}
                    <h2
                        className={cn(
                            'text-xl font-semibold leading-7 m-0 line-clamp-2',
                            capitalize && 'capitalize',
                        )}
                    >
                        {name || <>&mdash;</>}
                    </h2>
                    {contextLabel ? (
                        <span className="text-xs text-muted-foreground leading-4 truncate">
                            {contextPrefix ? `${contextPrefix} ` : null}
                            <span className="capitalize">{contextLabel}</span>
                        </span>
                    ) : null}
                </div>
                {actions.length ? (
                    <div className="flex items-center gap-1 shrink-0">
                        {actions}
                    </div>
                ) : null}
            </div>
        </div>
    );
}

export default DrawerHeader;
